import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  api,
  formatMoney,
  FY_MONTH_NAMES,
  type CampusSummary,
  type MonthFigure,
  type PeriodSummary,
  type Row,
} from "./api";
import { Figure, PositionSparkline, RunwayPill } from "./charts";
import { CampusChart } from "./CampusChart";

/**
 * The explorer, for the people allowed to see everything.
 *
 * Audience first: finance and leadership, who are entitled to every campus.
 * That takes per-campus authorization off the critical path, and it is the
 * audience for whom cross-campus comparison is the point rather than a leak.
 *
 * The static campus reports are **not** replaced by this. They stay inert —
 * no scripts, printable, readable from an email — because that is the property
 * that replaces the emailed spreadsheet tab. This sits beside them and links
 * to them.
 */
type View =
  | { name: "overview" }
  | { name: "campus"; sheet: string }
  // Carries where it was opened from, so Back returns to the campus rather
  // than dumping you at the top. Reached from two places, and guessing wrong
  // costs the reader their place in a long list.
  | { name: "job"; code: string; from: View }
  | { name: "compare" };

export default function App() {
  const [signedIn, setSignedIn] = useState<boolean | undefined>(undefined);
  const [periods, setPeriods] = useState<PeriodSummary[]>([]);
  const [period, setPeriod] = useState<string>("");
  const [view, setView] = useState<View>({ name: "overview" });

  useEffect(() => {
    api
      .session()
      .then(() => setSignedIn(true))
      .catch(() => setSignedIn(false));
  }, []);

  useEffect(() => {
    if (signedIn !== true) return;
    api.periods().then(({ periods: found }) => {
      setPeriods(found);
      setPeriod((current) => current || (found[0]?.period ?? ""));
    });
  }, [signedIn]);

  if (signedIn === undefined) return <div className="loading">…</div>;
  if (!signedIn) return <SignIn onSignedIn={() => setSignedIn(true)} />;

  const current = periods.find((entry) => entry.period === period);

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <strong>AFES finance</strong>
          <span className="muted">explorer</span>
        </div>
        <nav>
          <button
            className={view.name === "overview" ? "active" : ""}
            onClick={() => setView({ name: "overview" })}
          >
            Overview
          </button>
          <button
            className={view.name === "compare" ? "active" : ""}
            onClick={() => setView({ name: "compare" })}
          >
            What moved
          </button>
        </nav>
        <div className="topbar-right">
          <select value={period} onChange={(event) => setPeriod(event.target.value)}>
            {periods.map((entry) => (
              <option key={entry.period} value={entry.period}>
                {entry.label}
              </option>
            ))}
          </select>
          <button
            onClick={async () => {
              await api.signOut();
              setSignedIn(false);
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      {period === "" ? (
        <div className="loading">…</div>
      ) : (
        <main>
          {view.name === "overview" && (
            <Overview
              period={period}
              summary={current}
              onCampus={(sheet) => setView({ name: "campus", sheet })}
              onJob={(code) => setView({ name: "job", code, from: { name: "overview" } })}
            />
          )}
          {view.name === "campus" && (
            <Campus
              period={period}
              sheet={view.sheet}
              onBack={() => setView({ name: "overview" })}
              onJob={(code) => setView({ name: "job", code, from: view })}
            />
          )}
          {view.name === "job" && (
            <JobCode
              period={period}
              code={view.code}
              backLabel={view.from.name === "campus" ? view.from.sheet : "All campuses"}
              onBack={() => setView(view.from)}
            />
          )}
          {view.name === "compare" && <Compare period={period} summary={current} />}
        </main>
      )}
    </div>
  );
}

function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  return (
    <div className="signin">
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setError(undefined);
          try {
            await api.signIn(password);
            onSignedIn();
          } catch {
            setError("That password was not accepted.");
          } finally {
            setBusy(false);
          }
        }}
      >
        <h1>AFES finance</h1>
        <p className="muted">
          These pages show individual staff support balances. Sign in to continue.
        </p>
        <input
          type="password"
          value={password}
          autoFocus
          placeholder="Password"
          onChange={(event) => setPassword(event.target.value)}
        />
        <button type="submit" disabled={busy || password === ""}>
          {busy ? "Checking…" : "Sign in"}
        </button>
        {error !== undefined && <p className="error">{error}</p>}
      </form>
    </div>
  );
}

function useAsync<T>(load: () => Promise<T>, deps: unknown[]): {
  data: T | undefined;
  error: string | undefined;
} {
  const [data, setData] = useState<T | undefined>();
  const [error, setError] = useState<string | undefined>();
  const run = useCallback(load, deps);

  useEffect(() => {
    let live = true;
    setData(undefined);
    setError(undefined);
    run()
      .then((value) => live && setData(value))
      .catch((cause: unknown) =>
        live && setError(cause instanceof ApiError ? cause.message : "Something went wrong"),
      );
    return () => {
      live = false;
    };
  }, [run]);

  return { data, error };
}

function Overview({
  period,
  summary,
  onCampus,
  onJob,
}: {
  period: string;
  summary: PeriodSummary | undefined;
  onCampus: (sheet: string) => void;
  onJob: (code: string) => void;
}) {
  const { data, error } = useAsync(() => api.campuses(period), [period]);
  const deficit = useAsync(() => api.deficit(period), [period]);
  const [region, setRegion] = useState("all");

  if (error !== undefined) return <p className="error">{error}</p>;
  if (data === undefined) return <div className="loading">…</div>;

  const regions = [...new Set(data.campuses.map((campus) => campus.region))].sort();
  const visible =
    region === "all"
      ? data.campuses
      : data.campuses.filter((campus) => campus.region === region);

  const byRegion = new Map<string, CampusSummary[]>();
  for (const campus of visible) {
    byRegion.set(campus.region, [...(byRegion.get(campus.region) ?? []), campus]);
  }

  return (
    <>
      <section className="stats">
        <Stat label="Staff in deficit" value={summary?.staffInDeficit} tone="critical" />
        <Stat
          label="In deficit at some month end"
          value={summary?.everInDeficitThisYear}
          hint="Since October. A month-end level hides most of the year."
        />
        <Stat label="Slipped this month" value={summary?.crossedIntoDeficit} tone="serious" />
        <Stat label="Recovered this month" value={summary?.crossedOutOfDeficit} tone="good" />
      </section>

      {deficit.data !== undefined && deficit.data.unclassified.length > 0 && (
        <p className="notice">
          A further <strong>{deficit.data.unclassified.length}</strong> rows are in deficit
          but have no category recorded, so no staff count includes them —{" "}
          {deficit.data.unclassified.filter((row) => row.recipient === "Support Bucket").length}{" "}
          of those are support buckets. See <code>docs/findings.md</code>.
        </p>
      )}

      <div className="section-head">
        <h2>Campuses</h2>
        <label className="filter">
          Region{" "}
          <select value={region} onChange={(event) => setRegion(event.target.value)}>
            <option value="all">All ({data.campuses.length})</option>
            {regions.map((name) => (
              <option key={name} value={name}>
                {name} ({data.campuses.filter((campus) => campus.region === name).length})
              </option>
            ))}
          </select>
        </label>
      </div>
      {[...byRegion.entries()].map(([name, campuses]) => (
        <section key={name}>
          <h3>{name}</h3>
          <table>
            <thead>
              <tr>
                <th>Campus</th>
                <th className="num">Staff</th>
                <th className="num">In deficit</th>
                <th className="num">Ever</th>
                <th className="num">Moved</th>
                <th className="num">Overall position</th>
              </tr>
            </thead>
            <tbody>
              {campuses.map((campus) => (
                <tr key={campus.campusId}>
                  <td>
                    <button className="link" onClick={() => onCampus(campus.sheet)}>
                      {campus.campus}
                    </button>
                  </td>
                  <td className="num">{campus.activeStaff}</td>
                  <td className="num">
                    {campus.staffInDeficit > 0 ? (
                      <strong className="bad">{campus.staffInDeficit}</strong>
                    ) : (
                      <span className="muted">0</span>
                    )}
                  </td>
                  <td className="num muted">{campus.everInDeficitThisYear}</td>
                  <td className="num">
                    {campus.crossedIntoDeficit > 0 && (
                      <span className="bad">↓{campus.crossedIntoDeficit}</span>
                    )}{" "}
                    {campus.crossedOutOfDeficit > 0 && (
                      <span className="good">↑{campus.crossedOutOfDeficit}</span>
                    )}
                  </td>
                  <td className="num">
                    <Figure value={campus.overallPosition} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}

      {deficit.data !== undefined && (
        <>
          <h2>Everyone in deficit</h2>
          <RowTable rows={deficit.data.staff} onJob={onJob} />
        </>
      )}
    </>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number | undefined;
  hint?: string;
  tone?: string;
}) {
  return (
    <div className="stat">
      <div className={`stat-value ${tone ?? ""}`}>{value ?? "—"}</div>
      <div className="stat-label">{label}</div>
      {hint !== undefined && <div className="stat-hint">{hint}</div>}
    </div>
  );
}

function RowTable({ rows, onJob }: { rows: Row[]; onJob: (code: string) => void }) {
  if (rows.length === 0) return <p className="muted">Nobody.</p>;
  return (
    <div className="table-wrap">
    <table>
      <thead>
        <tr>
          <th>Job code</th>
          <th>Recipient</th>
          <th>Campus</th>
          <th className="num">Position</th>
          <th>Runway</th>
          <th className="num">Months short</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.rowId}>
            <td>
              <button className="link mono" onClick={() => onJob(row.jobCode)}>
                {row.jobCode}
              </button>
              {row.apportioned && <span className="tag" title="Shared job code">split</span>}
            </td>
            <td>{row.recipient}</td>
            <td className="muted">{row.campus}</td>
            <td className="num">
              <Figure value={row.overallPosition} />
            </td>
            <td>
              <RunwayPill months={row.monthsOfEquity} />
            </td>
            <td className="num muted">{row.monthsInDeficit}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function Campus({
  period,
  sheet,
  onBack,
  onJob,
}: {
  period: string;
  sheet: string;
  onBack: () => void;
  onJob: (code: string) => void;
}) {
  const { data, error } = useAsync(() => api.campus(period, sheet), [period, sheet]);
  if (error !== undefined) return <p className="error">{error}</p>;
  if (data === undefined) return <div className="loading">…</div>;

  return (
    <>
      <button className="link" onClick={onBack}>
        ← All campuses
      </button>
      <h2>{data.campus.campus}</h2>
      <section className="stats">
        <Stat label="Staff" value={data.campus.activeStaff} />
        <Stat label="In deficit" value={data.campus.staffInDeficit} tone="critical" />
        <Stat label="Ever this year" value={data.campus.everInDeficitThisYear} />
        <Stat label="Slipped" value={data.campus.crossedIntoDeficit} tone="serious" />
      </section>
      <p>
        Overall position <Figure value={data.campus.overallPosition} />.{" "}
        <a href={`/reports/${data.period}/index.html`} target="_blank" rel="noreferrer">
          Open the printable report ↗
        </a>
      </p>

      <h3>Running position through the year</h3>
      <CampusChart rows={data.rows} />

      <h3>Everyone on this tab</h3>
      <p className="muted">
        All {data.rows.length} rows, in the order the workbook lists them —{" "}
        {data.rows.filter((row) => row.isStaff).length} with a support target, and{" "}
        {data.rows.filter((row) => !row.isStaff).length} general, camps and fund buckets
        that count towards the campus position but towards nobody's balance.
      </p>
      <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Job code</th>
            <th>Recipient</th>
            <th className="num">Brought forward</th>
            <th className="num">Year to date</th>
            <th className="num">Position</th>
            <th>Runway</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <tr key={row.rowId}>
              <td>
                <button className="link mono" onClick={() => onJob(row.jobCode)}>
                  {row.jobCode}
                </button>
                {row.apportioned && (
                  <span className="tag" title="Shared job code">
                    split
                  </span>
                )}
              </td>
              <td>
                {row.recipient}
                {!row.isStaff && <span className="tag">no target</span>}
              </td>
              <td className="num">{formatMoney(row.broughtForward)}</td>
              <td className="num">
                <Figure value={row.ytdSurplus} />
              </td>
              <td className="num">
                <Figure value={row.overallPosition} />
              </td>
              <td>
                <RunwayPill months={row.monthsOfEquity} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <h3>In deficit</h3>
      <RowTable rows={data.inDeficit} onJob={onJob} />

      {data.unclassifiedInDeficit.length > 0 && (
        <>
          <h3>In deficit, but counted nowhere</h3>
          <p className="muted">
            These rows have no category recorded, so they are absent from every staff
            figure — including the one above and the campus report.
          </p>
          <RowTable rows={data.unclassifiedInDeficit} onJob={onJob} />
        </>
      )}
    </>
  );
}

function JobCode({
  period,
  code,
  backLabel,
  onBack,
}: {
  period: string;
  code: string;
  backLabel: string;
  onBack: () => void;
}) {
  const { data, error } = useAsync(() => api.job(period, code), [period, code]);
  if (error !== undefined) return <p className="error">{error}</p>;
  if (data === undefined) return <div className="loading">…</div>;

  return (
    <>
      <button className="link" onClick={onBack}>
        ← {backLabel}
      </button>
      <h2 className="mono">{code}</h2>
      {data.rows.length > 1 && (
        <p className="notice">
          This job code is shared by {data.rows.length} people on an agreed split. They
          have their own targets and balances and are never added together.
        </p>
      )}
      {data.rows.map((row) => (
        <section key={row.rowId} className="card">
          <header>
            <div>
              <strong>{row.recipient}</strong> <span className="muted">{row.campus}</span>
              {row.categoryAndDays !== "" && (
                <span className="tag mono">{row.categoryAndDays}</span>
              )}
            </div>
            <RunwayPill months={row.monthsOfEquity} />
          </header>
          <div className="figures">
            <div>
              <span className="muted">Brought forward</span>
              <Figure value={row.broughtForward} plain />
            </div>
            <div>
              <span className="muted">Year to date</span>
              <Figure value={row.ytdSurplus} />
            </div>
            <div>
              <span className="muted">Overall position</span>
              <Figure value={row.overallPosition} />
            </div>
            <div>
              <span className="muted">Annual target</span>
              <Figure value={row.annualSrt} plain />
            </div>
          </div>
          <h4>Running position</h4>
          <PositionSparkline months={row.months} width={520} height={80} />
          <MonthTable months={row.months} />
        </section>
      ))}
    </>
  );
}

function MonthTable({ months }: { months: MonthFigure[] }) {
  return (
    <div className="table-wrap">
    <table className="months">
      <thead>
        <tr>
          <th>Month</th>
          <th className="num">Income</th>
          <th className="num">Expense</th>
          <th className="num">Surplus</th>
          <th className="num">Position</th>
        </tr>
      </thead>
      <tbody>
        {months
          .filter((month) => month.position !== undefined)
          .map((month) => (
            <tr key={month.fyMonth}>
              <td>{FY_MONTH_NAMES[month.fyMonth - 1]}</td>
              <td className="num">{formatMoney(month.income)}</td>
              <td className="num">{formatMoney(month.expense)}</td>
              <td className="num">
                <Figure value={month.surplus} />
              </td>
              <td className="num">
                <Figure value={month.position} />
              </td>
            </tr>
          ))}
      </tbody>
    </table>
    </div>
  );
}

function Compare({ period, summary }: { period: string; summary: PeriodSummary | undefined }) {
  const latest = summary?.fyMonth ?? 10;
  const [from, setFrom] = useState(Math.max(1, latest - 1));
  const [to, setTo] = useState(latest);
  const { data, error } = useAsync(() => api.compare(period, from, to), [period, from, to]);

  const options = FY_MONTH_NAMES.slice(0, latest).map((name, index) => (
    <option key={index + 1} value={index + 1}>
      {name}
    </option>
  ));

  return (
    <>
      <h2>What moved</h2>
      <div className="controls">
        <label>
          From{" "}
          <select value={from} onChange={(event) => setFrom(Number(event.target.value))}>
            {options}
          </select>
        </label>
        <label>
          To{" "}
          <select value={to} onChange={(event) => setTo(Number(event.target.value))}>
            {options}
          </select>
        </label>
      </div>
      {error !== undefined && <p className="error">{error}</p>}
      {data !== undefined && (
        <>
          <p className="muted">
            {data.compared} rows compared. A month-end report shows a level; this shows the
            change, which is the only genuinely new information each month.
          </p>
          <div className="two-up">
            <div>
              <h3>Largest falls</h3>
              <ChangeTable changes={data.fell} />
            </div>
            <div>
              <h3>Largest rises</h3>
              <ChangeTable changes={data.rose} />
            </div>
          </div>
        </>
      )}
    </>
  );
}

function ChangeTable({ changes }: { changes: { jobCode: string; recipient: string; campus: string; change: string }[] }) {
  return (
    <div className="table-wrap">
    <table>
      <thead>
        <tr>
          <th>Job code</th>
          <th>Campus</th>
          <th className="num">Change</th>
        </tr>
      </thead>
      <tbody>
        {changes.map((change) => (
          <tr key={`${change.jobCode}-${change.campus}`}>
            <td className="mono">{change.jobCode}</td>
            <td className="muted">{change.campus}</td>
            <td className="num">
              <Figure value={change.change} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}
